import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import { getContactNumber, Phone } from 'phoneNumberUtils';
import { Form } from 'react-final-form';
import { User } from 'models/User';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { PhoneNumberSetupInitiatedEventProps } from 'models/MixpanelEvents';

import './PhoneMissingModal.styles.scss';

type PhoneMissingModalProps = {
  className?: string;
  onUserUpdate: (user: User) => void;
  onSuccess: () => void;
} & ModalProps;

const PhoneMissingModal: React.ForwardRefRenderFunction<
  ModalRef,
  PhoneMissingModalProps
> = (props, ref) => {
  const { className, onUserUpdate, onSuccess, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const { popServerError } = useContext(PopNotificationsContext);
  const { currentUser } = useContext(CurrentUserContext);

  const getMixpanelProps = useGetMixpanelProps();

  const { _id, phone } = currentUser || {};

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('phone-missing-modal', className);

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async ({ phone }: { phone: Phone }) => {
      try {
        mixpanelService.phoneNumberSetupInitiated(
          getMixpanelProps({
            phoneNumber: getContactNumber(phone),
          }) as PhoneNumberSetupInitiatedEventProps,
        );

        const {
          data: { user },
        } = await api.users.patchUser(_id, { phone });
        onUserUpdate(user);
        modal.close();
        onSuccess();
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, modal, getMixpanelProps, onSuccess, onUserUpdate, popServerError],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('verifyYourPhoneNumber')}
      {...rest}
    >
      <Form
        initialValues={{ phone }}
        onSubmit={onSubmit}
        render={({ submitting, invalid, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {submitting && <Loader />}

            <p>{t('addPhoneNumberAndVerify')}</p>

            <PhoneFieldWrapper required />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting || invalid}>
                {t('submit')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(PhoneMissingModal);
